import { useState, useEffect } from 'react';

/**
 * Device class type representing the detected device category
 */
export type DeviceClass = 'phone' | 'tablet' | 'desktop';

/**
 * Tablet-specific screen dimensions
 * Based on common tablet breakpoints and device specifications
 */
const TABLET_MIN_WIDTH = 600;
const TABLET_MAX_WIDTH = 1280;
const IPAD_PRO_WIDTH = 1024;
const IPAD_PRO_HEIGHT = 1366;

/**
 * Check for URL param override for development
 * Use ?forceMobile=true to force mobile detection
 */
function hasForceMobileParam(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('forceMobile') === 'true';
}

/**
 * Detects if the current device is a mobile phone (not a tablet)
 * Uses user agent detection with screen size as fallback
 *
 * Dev override: Add ?forceMobile=true to any URL to force mobile detection
 *
 * @returns {boolean} true if device is a phone, false otherwise
 */
export function isMobilePhone(): boolean {
  // Dev override via URL param
  if (hasForceMobileParam()) {
    return true;
  }

  const userAgent = navigator.userAgent || '';

  // Check for explicit tablet indicators first (to exclude them)
  const isTabletDevice = isTablet();
  if (isTabletDevice) {
    return false;
  }

  // Check user agent for mobile indicators
  const mobileKeywords = /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const hasMobileUA = mobileKeywords.test(userAgent);

  if (hasMobileUA) {
    return true;
  }

  return false;
}

/**
 * Detects if the current device is a tablet
 * Uses user agent detection combined with screen dimensions
 *
 * @returns {boolean} true if device is a tablet, false otherwise
 */
export function isTablet(): boolean {
  const userAgent = navigator.userAgent || '';
  const width = window.innerWidth;
  const height = window.innerHeight;

  // Check for iPad user agent
  const isIPad = /iPad/i.test(userAgent);

  // Check for iPad with "Request Desktop Website" enabled
  // These report as Mac but have iPad-specific screen sizes
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;
  const isIPadDesktopMode =
    /Macintosh/i.test(userAgent) &&
    maxTouchPoints > 1 &&
    ((width === IPAD_PRO_WIDTH && height === IPAD_PRO_HEIGHT) ||
      (height === IPAD_PRO_WIDTH && width === IPAD_PRO_HEIGHT) ||
      (width === 768 && height === 1024) ||
      (height === 768 && width === 1024) ||
      (width === 810 && height === 1080) ||
      (height === 810 && width === 1080) ||
      (width === 820 && height === 1180) ||
      (height === 820 && width === 1180));

  if (isIPad || isIPadDesktopMode) {
    return true;
  }

  // Check for Android tablets
  // Android tablets don't have "Mobile" in user agent
  const isAndroid = /Android/i.test(userAgent);
  const hasNoMobileKeyword = !/Mobile/i.test(userAgent);

  if (isAndroid && hasNoMobileKeyword) {
    return true;
  }

  // Check for tablet-sized screens with touch capability
  // This catches foldables in unfolded mode and other edge cases
  const minDimension = Math.min(width, height);
  const maxDimension = Math.max(width, height);

  const isTabletSize = minDimension >= TABLET_MIN_WIDTH && maxDimension <= TABLET_MAX_WIDTH;

  // Additional check: device has touch and is tablet-sized
  const hasTouch =
    'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

  if (isTabletSize && hasTouch && !isMobileUA()) {
    return true;
  }

  return false;
}

/**
 * Helper function to detect mobile user agent (phone or tablet)
 */
function isMobileUA(): boolean {
  const userAgent = navigator.userAgent || '';
  return /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

/**
 * React hook that returns the current device class
 * Automatically updates on window resize
 *
 * @returns {DeviceClass} The current device class: 'phone', 'tablet', or 'desktop'
 */
export function useDeviceClass(): DeviceClass {
  const getDeviceClass = (): DeviceClass => {
    if (isMobilePhone()) {
      return 'phone';
    }
    if (isTablet()) {
      return 'tablet';
    }
    return 'desktop';
  };

  const [deviceClass, setDeviceClass] = useState<DeviceClass>(getDeviceClass);

  useEffect(() => {
    const handleResize = () => {
      setDeviceClass(getDeviceClass());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return deviceClass;
}
